import React, { useState, useEffect } from "react";
import config from '../config/config';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";

const S = {
  white: '#ffffff',
  bg: '#f9fafb',
  border: '#e5e7eb',
  accent: '#111827',
  accentSoft: '#f3f4f6',
  sub: '#6b7280',
  muted: '#9ca3af',
  error: '#ef4444',
  errorBg: '#fef2f2',
  radius: '16px',
  radiusSm: '10px',
  shadow: '0 1px 3px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.06)',
  font: "'DM Sans', 'Helvetica Neue', sans-serif",
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: S.radiusSm,
    fontFamily: S.font,
    fontSize: '13.5px',
    fontWeight: 500,
    background: S.white,
    '& fieldset': { borderColor: S.border },
    '&:hover fieldset': { borderColor: '#9ca3af' },
    '&.Mui-focused fieldset': { borderColor: S.accent, borderWidth: '1.5px' },
    '&.Mui-disabled': { background: S.bg },
  },
  '& .MuiInputLabel-root': {
    fontFamily: S.font,
    fontSize: '13px',
    fontWeight: 500,
    color: S.sub,
  },
  '& .MuiInputLabel-root.Mui-focused': { color: S.accent },
};

const InvoicePaymentDialog = ({
  open,
  onClose,
  onSubmit,
  invoice,
  invoiceItems = [],
}) => {
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    const fetchItems = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(`${config.API_URL}/api/pullproductitems/Expense`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        const sortedItems = (res.data.invoiceItems || []).sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setItems(sortedItems);
      } catch (err) {
        console.error('Error fetching expense items:', err);
        setMessage('Failed to load items from the server.');
      }
    };
    if (open) fetchItems();
  }, [open]);

  useEffect(() => {
    if (open) {
      const initialRows = invoiceItems.map((item) => {
        const matchedItem = items.find((apiItem) => apiItem.name === item.description) || {};
        return {
          id: item.id,
          description: item.description || "",
          quantity: item.quantity || "1",
          total: item.total || matchedItem.price || "0.00",
          amountPaid: (
            parseFloat(item.total || matchedItem.price || 0) - parseFloat(item.paid || 0)
          ).toFixed(2),
          isNew: false,
        };
      });

      const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      setRows((prevRows) => {
        if (prevRows.length === 0 || prevRows.every((row) => !row.isNew)) return initialRows;
        return prevRows;
      });

      setInvoiceDate(formatDate(invoice.invoiceIssueDate));
      setDueDate(formatDate(invoice.invoiceDueDate));
      setPaymentNote(invoice.note || "");
      setErrors({});
      setMessage(null);
      setLoading(false);
    }
  }, [open, invoiceItems, invoice, items]);

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;

    if (field === "description") {
      const selectedItem = items.find((item) => item.name === value);
      if (selectedItem) {
        updatedRows[index].total = (
          parseFloat(selectedItem.price || 0) * parseFloat(updatedRows[index].quantity || 1)
        ).toFixed(2);
      } else {
        updatedRows[index].total = "0.00";
      }
    }

    if (field === "quantity") {
      const selectedItem = items.find((item) => item.name === updatedRows[index].description);
      if (selectedItem) {
        updatedRows[index].total = (
          parseFloat(selectedItem.price || 0) * parseFloat(value || 1)
        ).toFixed(2);
      }
    }

    setRows(updatedRows);
  };

  const handleAddRow = () => {
    setRows((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        description: "",
        quantity: "1",
        total: "0.00",
        amountPaid: "0.00",
        isNew: true,
      },
    ]);
  };

  const handleDeleteRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  const handleEditSave = async () => {
    setLoading(true);
    setErrors({});
    setMessage(null);

    const newErrors = {};
    rows.forEach((row, index) => {
      if (!row.description) newErrors[`description-${index}`] = "Description is required";
      if (!row.quantity || row.quantity <= 0) newErrors[`quantity-${index}`] = "Quantity must be greater than 0";
      if (!row.total || row.total < 0) newErrors[`total-${index}`] = "Total must be non-negative";
      if (!row.amountPaid || row.amountPaid < 0) newErrors[`amountPaid-${index}`] = "Amount Paid must be non-negative";
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      const payload = {
        invoice_id: invoice.id,
        invoice_date: invoiceDate,
        due_date: dueDate,
        payment_note: paymentNote,
        payments: rows.map((row) => ({
          ...(row.isNew ? {} : { item_id: row.id }),
          description: row.description,
          quantity: parseFloat(row.quantity),
          total: parseFloat(row.total),
          amount_paid: parseFloat(row.amountPaid),
        })),
      };

      const response = await onSubmit(payload);

      if (response?.errors) setErrors(response.errors);
      else if (response?.message) setMessage(response.message);
      if (!response?.errors) onClose();
    } catch (error) {
      setMessage("An error occurred during saving.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: S.radius,
          boxShadow: S.shadow,
          background: S.white,
          border: `1px solid ${S.border}`,
          fontFamily: S.font,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3.5,
          pt: 3,
          pb: 2.5,
          borderBottom: `1px solid ${S.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography
            sx={{ fontFamily: S.font, fontWeight: 700, fontSize: '17px', color: S.accent, letterSpacing: '-0.02em' }}
          >
            Edit Expense
          </Typography>
          {invoice?.invoiceNumber && (
            <Typography sx={{ fontFamily: S.font, fontSize: '12px', color: S.muted, mt: 0.3 }}>
              #{invoice.invoiceNumber}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            border: `1px solid ${S.border}`,
            color: S.sub,
            '&:hover': { background: S.bg },
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3.5, py: 3 }}>
      <br/> <br/>
        {message && (
          <Box
            sx={{
              mb: 2.5,
              px: 2,
              py: 1.5,
              borderRadius: S.radiusSm,
              background: S.errorBg,
              border: `1px solid #fecaca`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: S.error, flexShrink: 0 }} />
            <Typography sx={{ fontFamily: S.font, fontSize: '13px', color: S.error, fontWeight: 500 }}>
              {message}
            </Typography>
          </Box>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <TextField
              label="Invoice Date"
              type="date"
              fullWidth
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              error={!!errors.invoiceDate}
              helperText={errors.invoiceDate}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Due Date"
              type="date"
              fullWidth
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              error={!!errors.dueDate}
              helperText={errors.dueDate}
              sx={fieldSx}
            />
          </Grid>
        </Grid>

        <Typography
          sx={{
            fontFamily: S.font,
            fontSize: '11px',
            fontWeight: 700,
            color: S.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: 1.5,
          }}
        >
          Line Items
        </Typography>

        {rows.map((row, index) => (
          <Box
            key={row.id}
            sx={{
              background: S.bg,
              borderRadius: S.radiusSm,
              border: `1px solid ${S.border}`,
              px: 2.5,
              py: 2,
              mb: 1.5,
            }}
          >
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={3}>
                <FormControl fullWidth error={!!errors[`description-${index}`]} sx={fieldSx}>
                  <InputLabel id={`description-label-${index}`}>Description</InputLabel>
                  <Select
                    labelId={`description-label-${index}`}
                    value={items.find((item) => item.name === row.description) ? row.description : ""}
                    onChange={(e) => handleInputChange(index, "description", e.target.value)}
                    disabled={loading || !row.isNew}
                    label="Description"
                    sx={{ fontFamily: S.font, fontSize: '13.5px', fontWeight: 500 }}
                  >
                    <MenuItem value="" sx={{ fontFamily: S.font, fontSize: '13px' }}>Select an item</MenuItem>
                    {items.map((item) => (
                      <MenuItem key={item.id} value={item.name} sx={{ fontFamily: S.font, fontSize: '13px', fontWeight: 500 }}>
                        {item.name} — {parseFloat(item.price).toFixed(2)}
                      </MenuItem>
                    ))}
                    {!row.isNew && row.description && !items.find((item) => item.name === row.description) && (
                      <MenuItem value={row.description} disabled sx={{ fontFamily: S.font, fontSize: '13px' }}>
                        {row.description} (Not in list)
                      </MenuItem>
                    )}
                  </Select>
                  {!!errors[`description-${index}`] && (
                    <Typography sx={{ color: S.error, fontSize: '11.5px', mt: 0.5, ml: 1.5, fontFamily: S.font }}>
                      {errors[`description-${index}`]}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={2}>
                <TextField
                  label="Qty"
                  type="number"
                  fullWidth
                  value={row.quantity}
                  onChange={(e) => handleInputChange(index, "quantity", e.target.value)}
                  disabled={loading}
                  error={!!errors[`quantity-${index}`]}
                  helperText={errors[`quantity-${index}`]}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  label="Total"
                  type="number"
                  fullWidth
                  value={row.total}
                  onChange={(e) => handleInputChange(index, "total", e.target.value)}
                  disabled={loading || row.isNew}
                  error={!!errors[`total-${index}`]}
                  helperText={errors[`total-${index}`]}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', pt: '10px !important' }}>
                {row.isNew && (
                  <IconButton
                    onClick={() => handleDeleteRow(index)}
                    disabled={loading}
                    size="small"
                    sx={{
                      borderRadius: '8px',
                      border: `1px solid #fecaca`,
                      background: S.errorBg,
                      color: S.error,
                      width: 34,
                      height: 34,
                      '&:hover': { background: '#fee2e2' },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Box>
        ))}

        <Box sx={{ mt: 0.5, mb: 3 }}>
          {/* Add Item button commented out to match original */}
        </Box>

        <TextField
          label="Payment Note"
          fullWidth
          multiline
          rows={3}
          value={paymentNote}
          onChange={(e) => setPaymentNote(e.target.value)}
          disabled={loading}
          sx={fieldSx}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3.5,
          pb: 3.5,
          pt: 2,
          borderTop: `1px solid ${S.border}`,
          gap: 1.5,
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            fontFamily: S.font,
            fontWeight: 600,
            fontSize: '13.5px',
            color: S.sub,
            background: S.bg,
            border: `1px solid ${S.border}`,
            borderRadius: '9px',
            textTransform: 'none',
            px: 3,
            py: 1.1,
            '&:hover': { background: S.border },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleEditSave}
          disabled={loading}
          sx={{
            fontFamily: S.font,
            fontWeight: 600,
            fontSize: '13.5px',
            color: S.white,
            background: S.accent,
            borderRadius: '9px',
            textTransform: 'none',
            px: 3,
            py: 1.1,
            minWidth: 120,
            '&:hover': { background: '#374151' },
            '&.Mui-disabled': { background: '#d1d5db', color: S.white },
          }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: S.white }} /> : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePaymentDialog;